/* Cosmova theme – core (vanilla JS, no dependencies).
   Drawers & modals (focus trap), header, mobile menu, search overlay with predictive search,
   AJAX cart (Section Rendering API), quick view / quick add, product form & variants,
   wishlist & compare, popups, localization. */
(function () {
  'use strict';

  var T = window.theme || { routes: {}, strings: {}, settings: {} };
  var routes = T.routes || {};
  var strings = T.strings || {};
  var settings = T.settings || {};
  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- Utilities */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function sectionIdOf(el) { var s = el && el.closest('.shopify-section'); return s ? s.id.replace('shopify-section-', '') : null; }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); }
  function store(key, value) {
    try {
      if (value === undefined) return JSON.parse(localStorage.getItem(key) || 'null');
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) { return null; }
  }
  function fetchText(url, opts) { return fetch(url, opts).then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); }); }
  function withParam(url, key, value) { var u = new URL(url, window.location.origin); u.searchParams.set(key, value); return u.pathname + u.search; }
  function formatMoney(cents, format) {
    format = format || settings.moneyFormat || '{{amount_with_comma_separator}} €';
    var n = (Number(cents) || 0) / 100;
    function fmt(dec, thou, sep) { var p = n.toFixed(dec).split('.'); return p[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (dec ? sep + p[1] : ''); }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      if (k === 'amount') return fmt(2, ',', '.');
      if (k === 'amount_no_decimals') return fmt(0, ',', '.');
      if (k === 'amount_no_decimals_with_comma_separator') return fmt(0, '.', ',');
      return fmt(2, '.', ',');
    });
  }

  /* live region + toast */
  var live;
  function announce(msg) {
    if (!live) { live = document.createElement('div'); live.className = 'visually-hidden'; live.setAttribute('aria-live', 'polite'); live.setAttribute('role', 'status'); document.body.appendChild(live); }
    live.textContent = ''; setTimeout(function () { live.textContent = msg; }, 50);
  }
  var toastTimer;
  function toast(msg, isError) {
    var el = qs('[data-toast]');
    if (!el) { el = document.createElement('div'); el.className = 'toast'; el.setAttribute('data-toast', ''); el.setAttribute('role', 'status'); document.body.appendChild(el); }
    el.textContent = msg; el.classList.toggle('toast--error', !!isError); el.classList.add('is-visible');
    clearTimeout(toastTimer); toastTimer = setTimeout(function () { el.classList.remove('is-visible'); }, 2800);
  }

  /* registry so other bundles can hook into init and re-init on section reloads */
  var initializers = [];
  var Cosmova = window.Cosmova = {
    qs: qs, qsa: qsa, on: on, debounce: debounce, escapeHtml: escapeHtml, parseHTML: parseHTML, fetchText: fetchText,
    sectionIdOf: sectionIdOf, formatMoney: formatMoney, store: store, announce: announce, toast: toast, withParam: withParam,
    reduceMotion: reduceMotion, strings: strings, settings: settings, routes: routes,
    register: function (fn) { initializers.push(fn); if (Cosmova.ready) fn(document); }
  };

  /* ---------------------------------------------------------------- Focus trap */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), video[controls]';
  function trapFocus(container, e) {
    var items = qsa(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) { e.preventDefault(); container.focus(); return; }
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------------------------------------------------------------- Overlay stack: drawers & modals */
  var overlay, stack = [];
  function topLayer() { return stack[stack.length - 1] || null; }
  function lockScroll(lock) {
    if (lock) { root.style.setProperty('--scrollbar-w', (window.innerWidth - root.clientWidth) + 'px'); document.body.classList.add('scroll-lock'); }
    else document.body.classList.remove('scroll-lock');
  }
  function showOverlay(show) {
    overlay = overlay || qs('[data-page-overlay]');
    if (!overlay) return;
    if (show) { overlay.hidden = false; requestAnimationFrame(function () { overlay.classList.add('is-visible'); }); }
    else { overlay.classList.remove('is-visible'); setTimeout(function () { if (!stack.length) overlay.hidden = true; }, 300); }
  }
  function openLayer(el, opts) {
    if (!el) return false;
    opts = opts || {};
    if (stack.indexOf(el) > -1) return true;
    // drawers replace each other; modals stack on top
    if (el.classList.contains('drawer')) stack.slice().forEach(function (l) { if (l.classList.contains('drawer')) closeLayer(l, true); });
    el._returnFocus = opts.returnFocus || document.activeElement;
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    stack.push(el);
    lockScroll(true); showOverlay(true);
    qsa('[aria-controls="' + el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    var header = qs('[data-header]'); if (header && el.id === 'MenuDrawer') header.classList.add('menu-open');
    if (el.id === 'SearchDrawer') document.body.classList.add('search-open');
    setTimeout(function () { var f = opts.focus || qs('input[type="search"]', el) || qs('[data-autofocus]', el) || el; try { f.focus({ preventScroll: true }); } catch (err) { f.focus(); } }, 80);
    el.dispatchEvent(new CustomEvent('layer:open', { bubbles: true }));
    return true;
  }
  function closeLayer(el, silent) {
    el = el || topLayer(); if (!el) return;
    var i = stack.indexOf(el); if (i === -1) return;
    stack.splice(i, 1);
    el.classList.remove('is-open');
    var isModal = !el.classList.contains('drawer');
    setTimeout(function () { if (!el.classList.contains('is-open') && (isModal || el.hasAttribute('data-hide-closed'))) el.hidden = true; }, 320);
    qsa('[aria-controls="' + el.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (el.id === 'MenuDrawer') { var header = qs('[data-header]'); if (header) header.classList.remove('menu-open'); }
    if (el.id === 'SearchDrawer') document.body.classList.remove('search-open');
    qsa('video', el).forEach(function (v) { v.pause(); });
    if (!stack.length) { lockScroll(false); showOverlay(false); }
    if (!silent && el._returnFocus && el._returnFocus.focus && document.contains(el._returnFocus)) el._returnFocus.focus({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('layer:close', { bubbles: true }));
  }
  Cosmova.open = openLayer; Cosmova.close = closeLayer; Cosmova.layerCount = function () { return stack.length; };
  function openDrawer(id) { return openLayer(document.getElementById(id)); }

  on(document, 'click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      var id = opener.getAttribute('data-drawer-open');
      if (id === 'CartDrawer' && T.cartType !== 'drawer') return;
      if (openLayer(document.getElementById(id), { returnFocus: opener })) e.preventDefault();
      return;
    }
    var mOpen = e.target.closest('[data-modal-open]');
    if (mOpen) { e.preventDefault(); var m = document.getElementById(mOpen.getAttribute('data-modal-open')); if (m && m.parentElement !== document.body) document.body.appendChild(m); openLayer(m, { returnFocus: mOpen }); return; }
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-modal-close]')) { e.preventDefault(); closeLayer(e.target.closest('.drawer, .modal, [data-layer]') || topLayer()); return; }
    if (e.target.closest('[data-page-overlay]')) { closeLayer(); return; }
    var top = topLayer();
    if (top && top.classList.contains('modal') && e.target === top) closeLayer(top);
  });
  on(document, 'keydown', function (e) {
    var top = topLayer();
    if (e.key === 'Escape') {
      if (top) { closeLayer(top); return; }
      qsa('.hotspot.is-open').forEach(function (h) { h.classList.remove('is-open'); });
      qsa('[data-popover-panel]:not([hidden])').forEach(function (p) { togglePopover(p.closest('[data-popover]'), false); });
      qsa('[data-nav-item].is-open').forEach(function (n) { n.classList.remove('is-open'); });
    }
    if (e.key === 'Tab' && top) trapFocus(top, e);
  });

  /* ---------------------------------------------------------------- Announcement bar */
  function initAnnouncement(scope) {
    qsa('[data-announcement]', scope).forEach(function (bar) {
      if (bar.dataset.ready) return; bar.dataset.ready = '1';
      var section = bar.closest('.shopify-section');
      if (sessionStorage.getItem('cosmova-announcement-closed') === '1' && !(window.Shopify && window.Shopify.designMode)) { if (section) section.hidden = true; }
      var slides = qsa('.announcement__slide', bar);
      var i = 0, speed = parseInt(bar.dataset.autoplay, 10) || 5, paused = false;
      function show(n) { if (bar.dataset.shown) bar.classList.add('is-rotated'); bar.dataset.shown = '1'; i = (n + slides.length) % slides.length; slides.forEach(function (s, k) { s.classList.toggle('is-active', k === i); s.setAttribute('aria-hidden', String(k !== i)); }); }
      show(0);
      on(bar, 'mouseenter', function () { paused = true; }); on(bar, 'mouseleave', function () { paused = false; });
      on(bar, 'focusin', function () { paused = true; }); on(bar, 'focusout', function () { paused = false; });
      if (slides.length > 1 && !reduceMotion) setInterval(function () { if (!paused) show(i + 1); }, speed * 1000);
      on(qs('[data-announcement-prev]', bar), 'click', function () { show(i - 1); });
      on(qs('[data-announcement-next]', bar), 'click', function () { show(i + 1); });
      on(qs('[data-announcement-close]', bar), 'click', function () {
        if (section) section.hidden = true;
        sessionStorage.setItem('cosmova-announcement-closed', '1');
        setHeaderHeight();
      });
    });
  }

  /* ---------------------------------------------------------------- Countdown (shared) */
  function initCountdown(scope) {
    qsa('[data-countdown]', scope).forEach(function (el) {
      if (el.dataset.ready) return; el.dataset.ready = '1';
      var end = new Date(String(el.getAttribute('data-countdown')).trim().replace(' ', 'T')).getTime();
      if (isNaN(end)) return;
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      var timer;
      var tick = function () {
        var left = end - Date.now();
        if (left <= 0 && el.getAttribute('data-expired-text')) { el.textContent = el.getAttribute('data-expired-text'); clearInterval(timer); return; }
        var d = Math.max(0, left) / 1000;
        var set = function (sel, v) { var x = qs(sel, el); if (x) x.textContent = pad(v); };
        set('[data-days]', Math.floor(d / 86400)); set('[data-hours]', Math.floor(d % 86400 / 3600));
        set('[data-minutes]', Math.floor(d % 3600 / 60)); set('[data-seconds]', Math.floor(d % 60));
      };
      tick(); timer = setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------------- Header */
  function setHeaderHeight() {
    var h = qs('[data-header]');
    if (h) root.style.setProperty('--header-h', h.offsetHeight + 'px');
    var a = qs('.section-announcement'); root.style.setProperty('--announcement-h', (a && !a.closest('.shopify-section').hidden ? a.offsetHeight : 0) + 'px');
  }
  function togglePopover(wrap, force) {
    if (!wrap) return;
    var btn = qs('[data-popover-toggle]', wrap), panel = qs('[data-popover-panel]', wrap);
    var open = force !== undefined ? force : panel.hidden;
    panel.hidden = !open; btn.setAttribute('aria-expanded', String(open));
    if (open) { var f = qs(FOCUSABLE, panel); if (f) f.focus(); }
  }
  /* Menu / mega-menu images are rendered inert (data-src) so hidden UI never costs a request or becomes an LCP candidate;
     they are activated the first time the visitor reaches for the menu (hover, focus, touch, click). */
  function loadDeferredImages(scope) {
    qsa('img[data-deferred]', scope || document).forEach(function (img) {
      if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
      if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
      img.removeAttribute('data-deferred');
    });
  }
  Cosmova.loadDeferredImages = loadDeferredImages;
  function initHeader() {
    var header = qs('[data-header]');
    if (!header) return;
    var warm = function () { loadDeferredImages(document); };
    ['pointerenter', 'focusin', 'touchstart', 'click'].forEach(function (ev) { on(header, ev, warm, { once: true, passive: true }); });
    on(document, 'layer:open', warm, { once: true });
    /* Measure via ResizeObserver: the callback runs after the browser's own layout pass, so first paint is not blocked by a forced reflow. */
    if ('ResizeObserver' in window) {
      var hro = new ResizeObserver(setHeaderHeight); hro.observe(header);
      var ann = qs('.section-announcement'); if (ann) hro.observe(ann);
    } else { setHeaderHeight(); }
    var mode = header.getAttribute('data-sticky') || 'none';
    var lastY = window.scrollY, tick = false;
    function update() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 10);
      if (mode === 'scroll-up') {
        var hide = y > lastY && y > header.offsetHeight * 2 && !stack.length && !header.matches(':focus-within');
        header.classList.toggle('is-hidden', hide);
      }
      lastY = y; tick = false;
    }
    on(window, 'scroll', function () { if (!tick) { tick = true; requestAnimationFrame(update); } }, { passive: true });
    on(window, 'resize', debounce(setHeaderHeight, 100));
    update();
    // mega menu / dropdown: hover (CSS), touch, keyboard
    qsa('[data-nav-item]', header).forEach(function (item) {
      var link = qs('.nav__link', item);
      link.setAttribute('aria-expanded', 'false');
      var setOpen = function (open) { item.classList.toggle('is-open', open); link.setAttribute('aria-expanded', String(open)); header.classList.toggle('mega-open', !!qs('[data-nav-item].is-open', header)); };
      on(item, 'mouseenter', function () { setOpen(true); });
      on(item, 'mouseleave', function () { setOpen(false); });
      on(link, 'touchend', function (e) { if (!item.classList.contains('is-open')) { e.preventDefault(); qsa('[data-nav-item].is-open', header).forEach(function (o) { o.classList.remove('is-open'); }); setOpen(true); } });
      on(link, 'keydown', function (e) {
        if (e.key === 'ArrowDown' || ((e.key === 'Enter' || e.key === ' ') && !item.classList.contains('is-open') && e.altKey)) { e.preventDefault(); setOpen(true); var f = qs('.mega a, .dropdown a', item); if (f) f.focus(); }
      });
      on(item, 'focusout', function (e) { if (!item.contains(e.relatedTarget)) setOpen(false); });
    });
    // popovers (localization)
    on(document, 'click', function (e) {
      var t = e.target.closest('[data-popover-toggle]');
      if (t) { togglePopover(t.closest('[data-popover]')); return; }
      qsa('[data-popover]').forEach(function (p) { if (!p.contains(e.target)) { var panel = qs('[data-popover-panel]', p); if (panel && !panel.hidden) togglePopover(p, false); } });
    });
  }

  /* ---------------------------------------------------------------- Mobile menu: nested sliding panels */
  function initMenuDrawer(scope) {
    qsa('[data-menu-drawer]', scope).forEach(function (drawer) {
      if (drawer.dataset.ready) return; drawer.dataset.ready = '1';
      function show(id, back) {
        var next = document.getElementById(id); if (!next) return;
        var current = qs('[data-menu-panel].is-active', drawer);
        if (current === next) return;
        next.hidden = false;
        next.classList.toggle('from-left', !!back);
        requestAnimationFrame(function () {
          if (current) { current.classList.remove('is-active'); current.classList.toggle('to-left', !back); setTimeout(function () { if (!current.classList.contains('is-active')) current.hidden = true; current.classList.remove('to-left'); }, 300); }
          next.classList.add('is-active');
          var f = qs('.menu-panel__back, .mobile-nav__link', next); if (f) f.focus({ preventScroll: true });
        });
      }
      on(drawer, 'click', function (e) {
        var o = e.target.closest('[data-menu-open]');
        if (o) { e.preventDefault(); o.setAttribute('aria-expanded', 'true'); show(o.getAttribute('data-menu-open')); return; }
        var b = e.target.closest('[data-menu-back]');
        if (b) { e.preventDefault(); qsa('[data-menu-open][aria-expanded="true"]', drawer).forEach(function (x) { if (x.closest('.menu-panel') && document.getElementById(b.getAttribute('data-menu-back')).contains(x)) x.setAttribute('aria-expanded', 'false'); }); show(b.getAttribute('data-menu-back'), true); }
      });
      on(drawer, 'layer:close', function () { setTimeout(function () { var rootPanel = document.getElementById('MenuPanel-root'); qsa('[data-menu-panel]', drawer).forEach(function (p) { var isRoot = p === rootPanel; p.classList.toggle('is-active', isRoot); p.hidden = !isRoot; }); }, 320); });
    });
  }

  /* ---------------------------------------------------------------- Search overlay + predictive search */
  function initSearch() {
    var form = qs('[data-predictive-search]');
    if (!form) return;
    var drawer = form.closest('.search-drawer');
    var input = qs('input[type="search"]', form);
    var container = qs('[data-predictive-results-container]', drawer);
    var defaults = qs('[data-search-default]', drawer);
    var reset = qs('[data-search-reset]', form);
    var enabled = form.getAttribute('data-enabled') !== 'false';
    var controller, cache = {};
    function setResults(html) {
      container.innerHTML = html;
      var has = !!html;
      if (defaults) defaults.hidden = has;
      input.setAttribute('aria-expanded', String(has));
      initTabs(container);
    }
    function clear() { setResults(''); input.removeAttribute('aria-activedescendant'); }
    var run = debounce(function () {
      var q = input.value.trim();
      if (reset) reset.hidden = !q;
      if (!enabled) return;
      if (q.length < 2) { clear(); return; }
      if (cache[q]) { setResults(cache[q]); return; }
      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      container.classList.add('is-loading');
      var url = (routes.predictiveSearch || '/search/suggest') + '?q=' + encodeURIComponent(q) + '&section_id=predictive-search&resources[type]=product,collection,page,query&resources[limit]=4&resources[limit_scope]=each&resources[options][unavailable_products]=last';
      fetchText(url, controller ? { signal: controller.signal } : undefined).then(function (html) {
        var fresh = qs('[data-predictive-results]', parseHTML(html));
        cache[q] = fresh ? fresh.outerHTML : '';
        if (input.value.trim() === q) setResults(cache[q]);
      }).catch(function () {}).then(function () { container.classList.remove('is-loading'); });
    }, 220);
    on(input, 'input', run);
    on(input, 'focus', function () { if (input.value.trim().length >= 2 && !container.innerHTML) run(); });
    on(reset, 'click', function () { setTimeout(function () { input.value = ''; reset.hidden = true; clear(); input.focus(); }, 0); });
    // keyboard navigation through options
    on(form, 'keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      var opts = qsa('[role="option"]', container).filter(function (o) { return o.offsetParent !== null; });
      if (!opts.length) return;
      var cur = qs('[aria-selected="true"]', container), idx = opts.indexOf(cur);
      if (e.key === 'Enter') { if (cur) { e.preventDefault(); var a = cur.matches('a') ? cur : qs('a', cur); if (a) a.click(); } return; }
      e.preventDefault();
      idx = e.key === 'ArrowDown' ? (idx + 1) % opts.length : (idx - 1 + opts.length) % opts.length;
      if (cur) cur.setAttribute('aria-selected', 'false');
      opts[idx].setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', opts[idx].id);
      opts[idx].scrollIntoView({ block: 'nearest' });
    });
    // popular term chips fill the input
    on(drawer, 'click', function (e) {
      var chip = e.target.closest('[data-search-term]');
      if (chip && enabled) { e.preventDefault(); input.value = chip.getAttribute('data-search-term'); input.focus(); run(); }
    });
  }

  /* ---------------------------------------------------------------- Cart */
  function cartSectionIds() {
    var ids = [];
    var d = document.getElementById('CartDrawer'); if (d) ids.push(sectionIdOf(d));
    var p = qs('[data-cart-page]'); if (p) ids.push(sectionIdOf(p));
    return ids.filter(Boolean);
  }
  function renderSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id]; if (!html) return;
      var target = document.getElementById('shopify-section-' + id);
      if (!target) return;
      var doc = parseHTML(html);
      var fresh = doc.getElementById('shopify-section-' + id) || doc.body;
      // keep open panels (note / discount) and drawer state
      var drawer = qs('.drawer', target), wasOpen = drawer && drawer.classList.contains('is-open');
      var openPanels = qsa('[data-extras-toggle][aria-expanded="true"]', target).map(function (b) { return b.getAttribute('aria-controls'); });
      var upsell = qs('[data-upsell-results]', target), upsellHTML = upsell && upsell.innerHTML;
      var scrollBody = qs('.cart-drawer__body', target), scrollTop = scrollBody ? scrollBody.scrollTop : 0;
      var focusedLine = document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-line]');
      var focusedIdx = focusedLine && document.activeElement.matches('[data-qty-change]') ? qsa('[data-qty-change]', focusedLine).indexOf(document.activeElement) : -1;
      var focusedKey = focusedLine && focusedLine.getAttribute('data-key');
      target.innerHTML = fresh.innerHTML;
      var newDrawer = qs('.drawer', target);
      if (wasOpen && newDrawer) { newDrawer.classList.add('is-open'); var i = stack.indexOf(drawer); if (i > -1) stack[i] = newDrawer; }
      openPanels.forEach(function (pid) { var b = qs('[aria-controls="' + pid + '"]', target), p = document.getElementById(pid); if (b && p) { b.setAttribute('aria-expanded', 'true'); p.hidden = false; } });
      var nu = qs('[data-upsell-results]', target);
      if (nu && upsellHTML && !nu.innerHTML.trim() && qs('[data-upsell-url]', target)) { nu.innerHTML = upsellHTML; nu.dataset.loaded = '1'; }
      var nb = qs('.cart-drawer__body', target); if (nb) nb.scrollTop = scrollTop;
      if (focusedKey) { var line = qs('[data-key="' + focusedKey + '"]', target); var btn = line && qsa('[data-qty-change]', line)[focusedIdx]; if (btn) btn.focus({ preventScroll: true }); else if (newDrawer) newDrawer.focus({ preventScroll: true }); }
      initScope(target);
    });
  }
  function updateCount(count) {
    qsa('[data-cart-count]').forEach(function (el) { el.textContent = count; el.setAttribute('data-count', count); });
    qsa('[data-cart-link]').forEach(function (a) { if (strings.cartCount) a.setAttribute('aria-label', strings.cartCount.replace('[count]', count)); });
  }
  function cartRequest(path, body) {
    body = body || {};
    var ids = cartSectionIds();
    if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
    qsa('[data-cart-loading]').forEach(function (l) { l.classList.add('is-active'); });
    return fetch((routes.root || '/').replace(/\/$/, '') + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(function (res) {
        if (!res.ok || res.json.status) { var err = new Error(res.json.description || res.json.message || strings.error || 'Error'); err.data = res.json; throw err; }
        return res.json;
      })
      .finally(function () { qsa('[data-cart-loading]').forEach(function (l) { l.classList.remove('is-active'); }); });
  }
  function refreshCart() {
    return fetch((routes.cart || '/cart') + '.js').then(function (r) { return r.json(); }).then(function (c) { updateCount(c.item_count); document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: c } })); return c; });
  }
  function addToCart(items, button) {
    if (button) { button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true'); button.disabled = true; }
    return cartRequest('/cart/add.js', { items: items })
      .then(function (data) {
        var drawer = document.getElementById('CartDrawer');
        if (T.cartType !== 'drawer' || !drawer) {
          if (qs('[data-cart-page]')) { renderSections(data.sections); refreshCart(); return data; }
          window.location.href = routes.cart || '/cart'; return data;
        }
        renderSections(data.sections);
        refreshCart();
        var top = topLayer(); if (top && top.classList.contains('modal')) closeLayer(top, true);
        openLayer(document.getElementById('CartDrawer'), { returnFocus: button });
        announce(strings.added || 'Added to cart');
        return data;
      })
      .finally(function () { if (button) { button.classList.remove('is-loading'); button.removeAttribute('aria-busy'); button.disabled = false; } });
  }
  function changeLine(line, quantity, el) {
    var wrap = el && el.closest('.cart-item'); if (wrap) wrap.classList.add('is-loading');
    return cartRequest('/cart/change.js', { line: line, quantity: quantity })
      .then(function (data) { renderSections(data.sections); updateCount(data.item_count); document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } })); })
      .catch(function (err) { if (wrap) wrap.classList.remove('is-loading'); toast(err.message, true); });
  }
  function updateCart(body) {
    return cartRequest('/cart/update.js', body).then(function (data) { renderSections(data.sections); updateCount(data.item_count); return data; });
  }
  Cosmova.addToCart = addToCart; Cosmova.updateCart = updateCart; Cosmova.renderSections = renderSections; Cosmova.refreshCart = refreshCart;

  on(document, 'click', function (e) {
    var quick = e.target.closest('[data-quick-add]');
    if (quick) { e.preventDefault(); addToCart([{ id: parseInt(quick.getAttribute('data-quick-add'), 10), quantity: 1 }], quick).catch(function (err) { toast(err.message, true); }); return; }
    var qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      var line = qtyBtn.closest('[data-line]');
      if (line) { e.preventDefault(); changeLine(parseInt(line.getAttribute('data-line'), 10), parseInt(qtyBtn.getAttribute('data-qty-change'), 10), qtyBtn); return; }
      var box = qtyBtn.closest('.qty'); var input = box && qs('[data-qty-input]', box);
      if (input) { e.preventDefault(); input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(qtyBtn.getAttribute('data-qty-change'), 10)); input.dispatchEvent(new Event('change', { bubbles: true })); }
      return;
    }
    // note / discount panels
    var tg = e.target.closest('[data-extras-toggle]');
    if (tg) {
      var panel = document.getElementById(tg.getAttribute('aria-controls'));
      var open = tg.getAttribute('aria-expanded') !== 'true';
      qsa('[data-extras-toggle]', tg.closest('.cart-extras')).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); var p = document.getElementById(b.getAttribute('aria-controls')); if (p) p.hidden = true; });
      tg.setAttribute('aria-expanded', String(open)); if (panel) { panel.hidden = !open; if (open) { var f = qs('textarea, input', panel); if (f) f.focus(); } }
      return;
    }
    var closeP = e.target.closest('[data-extras-close]');
    if (closeP) { var pn = closeP.closest('.cart-extras__panel'); var b2 = qs('[aria-controls="' + pn.id + '"]'); pn.hidden = true; if (b2) { b2.setAttribute('aria-expanded', 'false'); b2.focus(); } toast(strings.saved || 'Saved'); return; }
    // discount codes
    var apply = e.target.closest('[data-discount-apply]');
    if (apply) {
      var wrapD = apply.closest('.cart-extras__panel'), inp = qs('[data-discount-input]', wrapD), errD = qs('[data-discount-error]', wrapD);
      var code = inp.value.trim(); if (!code) { inp.focus(); return; }
      var existing = qs('[data-discount-codes]', apply.closest('.cart-extras'));
      var codes = existing ? existing.getAttribute('data-discount-codes').split(',').filter(Boolean) : [];
      if (codes.indexOf(code) === -1) codes.push(code);
      apply.classList.add('is-loading');
      updateCart({ discount: codes.join(',') }).then(function (cart) {
        var found = (cart.discount_codes || []).filter(function (d) { return d.code.toLowerCase() === code.toLowerCase(); })[0];
        if (found && found.applicable === false) toast(strings.discountInvalid || 'Code not applicable', true);
        else announce(strings.discountApplied || 'Discount applied');
      }).catch(function () { if (errD) errD.hidden = false; }).finally(function () { apply.classList.remove('is-loading'); });
      return;
    }
    var rm = e.target.closest('[data-discount-remove]');
    if (rm) {
      var list = rm.closest('[data-discount-codes]');
      var left = list.getAttribute('data-discount-codes').split(',').filter(function (c) { return c && c !== rm.getAttribute('data-discount-remove'); });
      updateCart({ discount: left.join(',') }); return;
    }
    // copy discount code
    var copy = e.target.closest('[data-copy-code]');
    if (copy) {
      var txt = copy.getAttribute('data-copy-code');
      var done = function () { var old = copy.innerHTML; copy.textContent = copy.getAttribute('data-copied') || '✓'; setTimeout(function () { copy.innerHTML = old; }, 1800); };
      if (navigator.clipboard) navigator.clipboard.writeText(txt).then(done, done); else done();
    }
  });
  on(document, 'change', function (e) {
    var input = e.target.closest('[data-qty-input]');
    if (input) { var line = input.closest('[data-line]'); if (line) changeLine(parseInt(line.getAttribute('data-line'), 10), Math.max(0, parseInt(input.value, 10) || 0), input); return; }
    var gift = e.target.closest('[data-gift-wrap]');
    if (gift) {
      gift.disabled = true;
      var lineNo = parseInt(gift.getAttribute('data-gift-line'), 10) || 0;
      var p = gift.checked ? addToCartSilently(parseInt(gift.getAttribute('data-gift-wrap'), 10)) : changeLine(lineNo, 0);
      Promise.resolve(p).catch(function (err) { toast(err.message, true); gift.checked = !gift.checked; }).finally(function () { gift.disabled = false; });
    }
  });
  function addToCartSilently(id) {
    return cartRequest('/cart/add.js', { items: [{ id: id, quantity: 1 }] }).then(function (data) { renderSections(data.sections); refreshCart(); });
  }
  on(document, 'input', debounce(function (e) {
    var note = e.target.closest && e.target.closest('[data-cart-note]');
    if (note) fetch((routes.root || '/').replace(/\/$/, '') + '/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  }, 500));
  on(document, 'keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-discount-input]')) { e.preventDefault(); var b = qs('[data-discount-apply]', e.target.closest('.cart-extras__panel')); if (b) b.click(); }
  });

  /* cart upsell (complementary → related fallback) */
  function initUpsell(scope) {
    qsa('[data-upsell-url]', scope).forEach(function (box) {
      var list = qs('[data-upsell-results]', box);
      if (!list || list.dataset.loaded || list.innerHTML.trim()) return;
      list.dataset.loaded = '1';
      var load = function (url) { return fetchText(url).then(function (html) { var f = qs('[data-upsell-results]', parseHTML(html)); return f ? f.innerHTML.trim() : ''; }); };
      load(box.getAttribute('data-upsell-url')).then(function (html) { return html || load(box.getAttribute('data-upsell-fallback')); })
        .then(function (html) { list.innerHTML = html; var aside = box.closest('.cart-drawer__upsell'); if (aside && !html && !qs('.upsell-item--promo', box)) aside.hidden = true; initSliders(box.parentElement); })
        .catch(function () {});
    });
  }

  /* delivery estimate, automatic mode only (product page + cart): business days, skips weekends, respects cut-off hour.
     Manual text / fixed dates are rendered server-side by snippets/delivery-estimate.liquid without [data-delivery], so JS never touches them. */
  function initDelivery(scope) {
    qsa('[data-delivery]', scope).forEach(function (el) {
      if (el.dataset.ready) return; el.dataset.ready = '1';
      var min = parseInt(el.getAttribute('data-min'), 10); if (isNaN(min) || min < 0) min = 1;
      var max = parseInt(el.getAttribute('data-max'), 10); if (isNaN(max) || max < min) max = min;
      var cutoff = parseInt(el.getAttribute('data-cutoff'), 10); if (cutoff < 1) cutoff = NaN;
      var now = new Date(), start = new Date(now);
      if (!isNaN(cutoff) && now.getHours() >= cutoff) start.setDate(start.getDate() + 1);
      function addBusiness(d, n) { var x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); if (x.getDay() !== 0 && x.getDay() !== 6) n--; } return x; }
      var locale = root.lang || 'it';
      var f = function (d) { return d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }); };
      var tpl = el.getAttribute('data-template') || '';
      var text = qs('[data-delivery-text]', el);
      if (text && tpl) text.innerHTML = tpl.replace('[start]', '<strong>' + escapeHtml(f(addBusiness(start, min))) + '</strong>').replace('[end]', '<strong>' + escapeHtml(f(addBusiness(start, max))) + '</strong>');
      var co = qs('[data-delivery-cutoff]', el), ctpl = el.getAttribute('data-cutoff-template');
      if (co && ctpl && !isNaN(cutoff) && now.getHours() < cutoff && now.getDay() !== 0 && now.getDay() !== 6) {
        var mins = (cutoff - now.getHours()) * 60 - now.getMinutes();
        co.innerHTML = ctpl.replace('[time]', '<strong>' + Math.floor(mins / 60) + 'h ' + (mins % 60) + 'min</strong>');
      }
    });
  }

  /* ---------------------------------------------------------------- Product form & variants (product page + quick view) */
  function initProduct(scope) {
    qsa('[data-product-section], [data-product]', scope).forEach(function (sec) {
      if (sec.dataset.ready) return; sec.dataset.ready = '1';
      var form = qs('form.product-form', sec) || document.getElementById('ProductForm-' + sec.getAttribute('data-section-id'));
      var jsonEl = qs('[data-variants-json]', sec);
      var variants = []; try { variants = jsonEl ? JSON.parse(jsonEl.textContent) : []; } catch (err) {}
      var updateHistory = sec.getAttribute('data-history') !== 'false';

      if (form) {
        on(form, 'submit', function (e) {
          if (T.cartType !== 'drawer' && !qs('[data-cart-page]')) return;
          e.preventDefault();
          var btn = qs('[data-add-to-cart]', form);
          var errBox = qs('[data-form-error]', form);
          var fd = new FormData(form);
          var item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 };
          var props = {}; fd.forEach(function (v, k) { var m = k.match(/^properties\[(.+)\]$/); if (m && v) props[m[1]] = v; });
          if (Object.keys(props).length) item.properties = props;
          if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
          if (errBox) errBox.hidden = true;
          addToCart([item], btn).catch(function (err) { if (errBox) { errBox.textContent = err.message; errBox.hidden = false; } else toast(err.message, true); });
        });
      }

      function currentOptions() {
        var opts = [];
        qsa('[data-option-index]', sec).forEach(function (el) {
          var idx = parseInt(el.getAttribute('data-option-index'), 10);
          if (el.tagName === 'SELECT') opts[idx] = el.value;
          else if (el.checked) opts[idx] = el.value;
        });
        return opts;
      }
      function markAvailability(opts) {
        // dim option values that have no available variant together with the other selected options
        qsa('[data-option-index]', sec).forEach(function (el) {
          var idx = parseInt(el.getAttribute('data-option-index'), 10);
          var check = function (value) { return variants.some(function (v) { return v.available && v.options[idx] === value && v.options.every(function (o, i) { return i === idx || opts[i] === undefined || o === opts[i]; }); }); };
          if (el.tagName === 'SELECT') qsa('option', el).forEach(function (o) { o.classList.toggle('is-unavailable', !check(o.value)); });
          else el.closest('.pill, .swatch') && el.closest('.pill, .swatch').classList.toggle('is-unavailable', !check(el.value));
        });
        qsa('[data-swatch-for]', sec).forEach(function (r) {
          var sel = document.getElementById(r.getAttribute('data-swatch-for')); if (!sel) return;
          var idx = parseInt(sel.getAttribute('data-option-index'), 10);
          var ok = variants.some(function (v) { return v.available && v.options[idx] === r.value && v.options.every(function (o, i) { return i === idx || opts[i] === undefined || o === opts[i]; }); });
          r.closest('.swatch').classList.toggle('is-unavailable', !ok);
        });
      }
      function onVariantChange() {
        var opts = currentOptions();
        var v = variants.find(function (x) { return x.options.every(function (o, i) { return opts[i] === undefined || o === opts[i]; }); });
        markAvailability(opts);
        qsa('[data-option-index]', sec).forEach(function (el) {
          var idx = parseInt(el.getAttribute('data-option-index'), 10);
          var block = el.closest('.product__block');
          var label = block && qs('[data-option-label]', block);
          if (label && opts[idx] !== undefined) label.textContent = opts[idx];
          if (el.tagName === 'SELECT') {
            var opt = el.options[el.selectedIndex];
            var sw = block && qs('[data-select-swatch]', block);
            if (sw && opt && opt.dataset.swatch) sw.setAttribute('style', opt.dataset.swatch);
            qsa('[data-swatch-for="' + el.id + '"]', sec).forEach(function (r) { r.checked = r.value === el.value; });
          }
        });
        var btns = qsa('[data-add-to-cart], [data-sticky-btn]', document).filter(function (b) { return sec.contains(b) || (form && b.getAttribute('form') === form.id); });
        var btnText = form && qs('[data-add-to-cart-text]', form);
        if (!v) {
          btns.forEach(function (b) { b.disabled = true; });
          if (btnText) btnText.textContent = strings.unavailable || 'Unavailable';
          return;
        }
        var idInput = form && qs('[data-variant-id]', form); if (idInput) idInput.value = v.id;
        btns.forEach(function (b) { b.disabled = !v.available; if (b.hasAttribute('data-sticky-btn')) b.textContent = v.available ? strings.addToCart : strings.soldOut; });
        if (btnText) btnText.textContent = v.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
        if (updateHistory && window.history.replaceState) { var u = new URL(window.location.href); u.searchParams.set('variant', v.id); window.history.replaceState({}, '', u.toString()); }
        if (v.media) {
          var fig = qs('[data-media-id="' + v.media + '"]', sec);
          if (fig) { var g = fig.parentElement; if (g.scrollWidth > g.clientWidth + 4) g.scrollTo({ left: fig.offsetLeft - g.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' }); else if (!sec.hasAttribute('data-product') && fig.getBoundingClientRect().top < 0) fig.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }); }
        }
        sec.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: v, form: form } }));
        // re-render server-driven pieces (price, stock, pickup, sku)
        var key = sec.getAttribute('data-section-key') || sectionIdOf(sec);
        var url = (sec.getAttribute('data-product-url') || window.location.pathname) + '?variant=' + v.id + '&section_id=' + key;
        fetchText(url).then(function (html) {
          var doc = parseHTML(html);
          ['[data-product-price]', '[data-inventory]', '[data-product-sku]', '.pickup'].forEach(function (sel) {
            var fresh = qsa(sel, doc), cur = qsa(sel, sec);
            cur.forEach(function (el, i) { if (fresh[i]) el.innerHTML = fresh[i].innerHTML; });
          });
          var freshModal = qs('[id^="PickupModal-"]', doc), curModal = qs('[id^="PickupModal-"]');
          if (freshModal && curModal) curModal.innerHTML = freshModal.innerHTML;
        }).catch(function () {});
      }
      on(sec, 'change', function (e) {
        var swatch = e.target.closest('[data-swatch-for]');
        if (swatch) { var sel = document.getElementById(swatch.getAttribute('data-swatch-for')); if (sel) { sel.value = swatch.value; } onVariantChange(); return; }
        if (e.target.closest('[data-option-index]')) onVariantChange();
      });
      markAvailability(currentOptions());
      // shade-group filter
      qsa('[data-swatch-filter]', sec).forEach(function (bar) {
        on(bar, 'click', function (e) {
          var b = e.target.closest('[data-group]'); if (!b) return;
          qsa('[data-group]', bar).forEach(function (x) { x.setAttribute('aria-pressed', String(x === b)); });
          var group = b.getAttribute('data-group');
          qsa('.swatch', bar.nextElementSibling).forEach(function (s) { s.hidden = (' ' + s.getAttribute('data-groups') + ' ').indexOf(' ' + group + ' ') === -1; });
        });
      });
    });
  }

  /* ---------------------------------------------------------------- Quick view modal */
  var quickModal;
  function getQuickModal() {
    if (quickModal) return quickModal;
    quickModal = document.createElement('div');
    quickModal.className = 'modal modal--quick';
    quickModal.id = 'QuickViewModal';
    quickModal.setAttribute('role', 'dialog'); quickModal.setAttribute('aria-modal', 'true'); quickModal.setAttribute('aria-label', strings.quickView || 'Quick view');
    quickModal.hidden = true; quickModal.tabIndex = -1;
    quickModal.innerHTML = '<div class="modal__panel modal__panel--quick"><button type="button" class="icon-btn modal__close" data-modal-close aria-label="' + escapeHtml(strings.close || 'Close') + '"><svg class="icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button><div class="modal__content" data-quick-content><div class="quick-skeleton" aria-hidden="true"><span></span><span></span></div></div></div>';
    document.body.appendChild(quickModal);
    return quickModal;
  }
  function openQuickView(url, trigger) {
    var m = getQuickModal(), content = qs('[data-quick-content]', m);
    content.innerHTML = '<div class="quick-skeleton" aria-hidden="true"><span></span><span></span></div>';
    openLayer(m, { returnFocus: trigger });
    fetchText(withParam(url, 'section_id', 'quick-view')).then(function (html) {
      var doc = parseHTML(html), fresh = qs('.quick', doc);
      if (!fresh) throw new Error('missing');
      content.innerHTML = fresh.outerHTML;
      var title = qs('[id^="QuickTitle-"]', content); if (title) { m.removeAttribute('aria-label'); m.setAttribute('aria-labelledby', title.id); }
      initScope(content);
      var first = qs('input:checked, select, [data-add-to-cart]', content); if (first) first.focus({ preventScroll: true });
    }).catch(function () { window.location.href = url; });
  }
  on(document, 'click', function (e) {
    var t = e.target.closest('[data-quick-view]');
    if (t) { e.preventDefault(); openQuickView(t.getAttribute('data-quick-view'), t); }
  });

  /* ---------------------------------------------------------------- Wishlist & compare (localStorage) */
  var WL = 'cosmova-wishlist', CMP = 'cosmova-compare';
  function getList(key) { var l = store(key); return Array.isArray(l) ? l : []; }
  function syncWishlist() {
    var list = getList(WL);
    qsa('[data-wishlist-toggle]').forEach(function (b) {
      var on_ = list.indexOf(b.getAttribute('data-wishlist-toggle')) > -1;
      b.setAttribute('aria-pressed', String(on_));
      b.setAttribute('aria-label', b.getAttribute(on_ ? 'data-label-remove' : 'data-label-add'));
    });
    qsa('[data-wishlist-count]').forEach(function (c) { c.textContent = list.length; c.setAttribute('data-count', list.length); c.hidden = !list.length; });
  }
  function syncCompare() {
    var list = getList(CMP);
    qsa('[data-compare-toggle]').forEach(function (b) { b.setAttribute('aria-pressed', String(list.indexOf(b.getAttribute('data-compare-toggle')) > -1)); });
    var bar = qs('[data-compare-bar]');
    if (!bar) return;
    bar.hidden = !list.length;
    var count = qs('[data-compare-bar-count]', bar); if (count) count.textContent = list.length;
    var thumbs = qs('[data-compare-bar-items]', bar);
    if (thumbs) thumbs.innerHTML = list.map(function (h) { return '<li><button type="button" class="compare-bar__item" data-compare-toggle="' + escapeHtml(h) + '" aria-pressed="true" aria-label="' + escapeHtml((strings.compareRemove || 'Remove') + ' ' + h) + '">' + escapeHtml(h.replace(/-/g, ' ')) + ' ×</button></li>'; }).join('');
  }
  Cosmova.getList = getList; Cosmova.syncWishlist = syncWishlist; Cosmova.syncCompare = syncCompare;
  on(document, 'click', function (e) {
    var w = e.target.closest('[data-wishlist-toggle]');
    if (w) {
      e.preventDefault();
      var h = w.getAttribute('data-wishlist-toggle'), list = getList(WL), i = list.indexOf(h);
      if (i > -1) list.splice(i, 1); else list.unshift(h);
      store(WL, list); syncWishlist();
      toast(i > -1 ? (strings.wishlistRemoved || 'Removed from wishlist') : (strings.wishlistAdded || 'Saved to wishlist'));
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list: list } }));
      return;
    }
    var c = e.target.closest('[data-compare-toggle]');
    if (c) {
      e.preventDefault();
      var ch = c.getAttribute('data-compare-toggle'), cl = getList(CMP), ci = cl.indexOf(ch), max = parseInt(settings.compareMax, 10) || 4;
      if (ci > -1) cl.splice(ci, 1); else { if (cl.length >= max) { toast((strings.compareMax || 'Max [max]').replace('[max]', max), true); return; } cl.push(ch); }
      store(CMP, cl); syncCompare();
      document.dispatchEvent(new CustomEvent('compare:change', { detail: { list: cl } }));
      return;
    }
    if (e.target.closest('[data-compare-clear]')) { store(CMP, []); syncCompare(); document.dispatchEvent(new CustomEvent('compare:change', { detail: { list: [] } })); }
  });
  on(window, 'storage', function (e) { if (e.key === WL) syncWishlist(); if (e.key === CMP) syncCompare(); });

  /* ---------------------------------------------------------------- Recommendations, sliders, tabs */
  function initRecommendations(scope) {
    qsa('[data-recommendations]', scope).forEach(function (box) {
      if (box.dataset.ready || box.children.length) return; box.dataset.ready = '1';
      var load = function () {
        fetchText(box.getAttribute('data-recommendations')).then(function (html) {
          var fresh = qs('[data-recommendations]', parseHTML(html));
          if (fresh && fresh.innerHTML.trim()) { box.innerHTML = fresh.innerHTML; initScope(box); }
        }).catch(function () {});
      };
      if ('IntersectionObserver' in window) { var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '600px 0px' }); io.observe(box); } else load();
    });
  }
  function initSliders(scope) {
    qsa('[data-slider]', scope).forEach(function (slider) {
      if (slider.dataset.ready) return; slider.dataset.ready = '1';
      var host = slider.closest('.complementary, .cart-drawer__upsell, .section, .stories, .recently') || slider.parentElement;
      var prev = qs('[data-slider-prev]', host), next = qs('[data-slider-next]', host);
      function step() { var f = slider.firstElementChild; if (!f) return slider.clientWidth; var gap = parseFloat(getComputedStyle(slider).columnGap) || 16; return f.getBoundingClientRect().width + gap; }
      function update() {
        if (prev) prev.disabled = slider.scrollLeft <= 2;
        if (next) next.disabled = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 2;
      }
      on(prev, 'click', function () { slider.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
      on(next, 'click', function () { slider.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
      on(slider, 'scroll', debounce(update, 60), { passive: true });
      on(window, 'resize', debounce(update, 120));
      update();
      var auto = parseInt(slider.getAttribute('data-autoplay'), 10);
      if (auto && !reduceMotion) {
        var paused = false;
        on(slider, 'mouseenter', function () { paused = true; }); on(slider, 'mouseleave', function () { paused = false; });
        on(slider, 'focusin', function () { paused = true; }); on(slider, 'focusout', function () { paused = false; });
        setInterval(function () { if (paused || document.hidden) return; if (slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 2) slider.scrollTo({ left: 0, behavior: 'smooth' }); else slider.scrollBy({ left: step(), behavior: 'smooth' }); }, auto * 1000);
      }
    });
  }
  function initTabs(scope) {
    qsa('[data-tabs]', scope).forEach(function (wrap) {
      if (wrap.dataset.ready) return; wrap.dataset.ready = '1';
      var host = wrap.matches('[role="tablist"]') ? wrap.parentElement : wrap;
      var tabs = qsa('[data-tab]', host);
      tabs.forEach(function (tab) {
        var activate = function () {
          var id = tab.getAttribute('data-tab');
          tabs.forEach(function (t) { t.setAttribute('aria-pressed', String(t === tab)); t.setAttribute('aria-selected', String(t === tab)); });
          qsa('[data-tab-panel]', host).forEach(function (p) { p.hidden = p.getAttribute('data-tab-panel') !== id; });
        };
        on(tab, 'click', activate);
        if (tab.closest('.mega')) on(tab, 'mouseenter', activate);
        on(tab, 'keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          var i = tabs.indexOf(tab), n = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
          n.focus(); n.click();
        });
      });
    });
  }

  /* ---------------------------------------------------------------- Customer pages, footer, localization */
  function initMisc(scope) {
    qsa('[data-toggle-recover]', scope).forEach(function (a) {
      on(a, 'click', function (e) { e.preventDefault(); var r = document.getElementById('RecoverPassword'), l = document.getElementById('CustomerLogin'); if (r && l) { r.hidden = !r.hidden; l.hidden = !r.hidden; } });
    });
    if (window.location.hash === '#recover') { var r = document.getElementById('RecoverPassword'), l = document.getElementById('CustomerLogin'); if (r && l) { r.hidden = false; l.hidden = true; } }
    qsa('[data-address-toggle]', scope).forEach(function (b) {
      on(b, 'click', function () { var t = document.getElementById(b.getAttribute('data-address-toggle')); if (t) { t.hidden = !t.hidden; b.setAttribute('aria-expanded', String(!t.hidden)); } });
    });
    qsa('[data-confirm-delete]', scope).forEach(function (f) { on(f, 'submit', function (e) { if (!window.confirm(f.getAttribute('data-confirm-delete'))) e.preventDefault(); }); });
    qsa('select[name="address[country]"]', scope).forEach(function (country) {
      var form = country.closest('form'), province = form && qs('select[name="address[province]"]', form);
      if (country.dataset.default) country.value = country.dataset.default;
      var fill = function () {
        if (!province) return;
        var opt = country.options[country.selectedIndex];
        var list = []; try { list = JSON.parse(opt && opt.getAttribute('data-provinces') || '[]'); } catch (err) {}
        province.innerHTML = list.map(function (p) { return '<option value="' + escapeHtml(p[0]) + '">' + escapeHtml(p[1]) + '</option>'; }).join('');
        province.closest('.field').hidden = !list.length;
        if (province.dataset.default) province.value = province.dataset.default;
      };
      on(country, 'change', fill); fill();
    });
    if (window.matchMedia('(max-width: 639px)').matches) qsa('[data-footer-accordion]', scope).forEach(function (d) { d.open = false; });
    qsa('[data-footer-accordion] > summary', scope).forEach(function (sm) { on(sm, 'click', function (e) { if (!window.matchMedia('(max-width: 639px)').matches) e.preventDefault(); }); });
    qsa('select[data-autosubmit]', scope).forEach(function (s) { if (s.dataset.ready) return; s.dataset.ready = '1'; on(s, 'change', function () { s.form.submit(); }); });
    qsa('[data-scroll-to]', scope).forEach(function (b) { on(b, 'click', function () { var t = document.getElementById(b.getAttribute('data-scroll-to')); if (t) t.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' }); }); });
  }

  /* ---------------------------------------------------------------- Init */
  function initScope(scope) {
    initAnnouncement(scope); initCountdown(scope); initMenuDrawer(scope); initProduct(scope); initRecommendations(scope);
    initSliders(scope); initTabs(scope); initUpsell(scope);
    initDelivery(scope); initMisc(scope);
    initializers.forEach(function (fn) { try { fn(scope); } catch (err) { if (window.console) console.error(err); } });
    syncWishlist(); syncCompare();
  }
  Cosmova.initScope = initScope;
  /* Non-critical UI (newsletter popup, age notice, back-to-top) lives in overlays.js and is
     fetched only when the browser is idle after load, so it never competes with LCP or input handling. */
  function loadOverlays() {
    var src = window.theme && window.theme.assets && window.theme.assets.overlays;
    if (!src || Cosmova.overlaysRequested) return;
    if (!qs('[data-popup], [data-age-gate], [data-back-to-top]')) return;
    Cosmova.overlaysRequested = true;
    var sc = document.createElement('script'); sc.src = src; sc.defer = true; document.head.appendChild(sc);
  }
  function whenIdle(fn) {
    var run = function () { if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2500 }); else setTimeout(fn, 300); };
    if (document.readyState === 'complete') run(); else on(window, 'load', run);
  }
  /* Loox injects its "Scroll to reviews" anchor without an href (not crawlable, fails SEO audit): give it the in-page target. */
  function fixLooxLink() {
    var fix = function () { qsa('a[aria-label="Scroll to reviews"]:not([href])').forEach(function (a) { a.setAttribute('href', '#looxReviews'); }); };
    fix();
    var hosts = qsa('[id*="loox_rating"]');
    if (hosts.length && 'MutationObserver' in window) {
      var mo = new MutationObserver(fix);
      hosts.forEach(function (n) { mo.observe(n, { childList: true, subtree: true }); });
      setTimeout(function () { mo.disconnect(); }, 20000);
    }
  }
  /* Footer "Preferenze privacy": shown only when Shopify's native privacy banner exposes showPreferences(). */
  function initPrivacyPrefs() {
    var items = qsa('[data-privacy-prefs-item]'); if (!items.length) return;
    var tries = 0;
    (function check() {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.showPreferences === 'function') {
        items.forEach(function (li) {
          li.hidden = false;
          var b = qs('[data-privacy-prefs]', li);
          if (b && !b.dataset.ready) { b.dataset.ready = '1'; on(b, 'click', function () { cp.showPreferences(); }); }
        });
      } else if (++tries < 20) setTimeout(check, 500);
    })();
  }
  function init() {
    fixLooxLink(); initPrivacyPrefs(); initHeader(); initSearch(); initScope(document); Cosmova.ready = true;
    if (window.Shopify && window.Shopify.designMode) loadOverlays(); else whenIdle(loadOverlays);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  // Theme editor support
  on(document, 'shopify:section:load', function (e) { initScope(e.target); setHeaderHeight(); });
  on(document, 'shopify:section:unload', function (e) { stack.slice().forEach(function (l) { if (e.target.contains(l)) closeLayer(l, true); }); });
  on(document, 'shopify:section:select', function (e) {
    var p = qs('[data-popup], [data-age-gate]', e.target); if (p) openLayer(p);
    if (qs('#CartDrawer', e.target) && T.cartType === 'drawer') openDrawer('CartDrawer');
  });
  on(document, 'shopify:section:deselect', function (e) { var l = qs('[data-popup], [data-age-gate], #CartDrawer', e.target); if (l) closeLayer(l, true); });
  on(document, 'shopify:block:select', function (e) { var n = e.target.closest('[data-nav-item]'); if (n) n.classList.add('is-open'); var s = e.target.closest('.announcement__slide'); if (s) { qsa('.announcement__slide', s.parentElement).forEach(function (x) { x.classList.toggle('is-active', x === s); }); } });
  on(document, 'shopify:block:deselect', function (e) { var n = e.target.closest('[data-nav-item]'); if (n) n.classList.remove('is-open'); });
})();
